import { usesOfficeDashboard } from './seesAgencyListings';
import { rememberLoginHome } from './termsConsent';
import { rememberLoginExperience } from './authExperience';

/**
 * Persist the logged-in / switched user into localStorage.
 *
 * REGRESSION (do not "simplify" this):
 * Large agencies (Team Apex, 8k+ embedded CRM leads, licence PDFs, data-URL
 * photos) cannot be JSON.stringified into localStorage. An older patch tried
 * the full payload first and only slimmed on QuotaExceededError. Stringifying
 * those docs throws RangeError instead, which showed up as
 * "Could not switch account". Always slim FIRST.
 *
 * Leads still load via /api/clients and the dashboard - they must not live on
 * the session blob.
 */

function isDataUrl(v) {
  return typeof v === 'string' && v.startsWith('data:');
}

/** Keep agency logos / headshots that fit in localStorage; drop licence-sized data URLs. */
const MAX_SESSION_DATA_URL = 80000;

function sessionMedia(v) {
  if (!v || typeof v !== 'string') return v;
  if (!isDataUrl(v)) return v;
  return v.length <= MAX_SESSION_DATA_URL ? v : undefined;
}

function isQuotaError(err) {
  return Boolean(
    err
    && (
      err.name === 'QuotaExceededError'
      || err.name === 'RangeError'
      || err.code === 22
      || err.code === 1014
      || /invalid string length|quota/i.test(err.message || '')
    ),
  );
}

function slimSession(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  if (data.token) {
    next.sessionIssuedAt = Date.now();
  }
  const stripMerged = (v) => (typeof v === 'string' ? v.replace(/\s*\(\s*merged into\s+[^)]+\)\s*$/i, '').trim() : v);
  next.name = stripMerged(next.name);
  next.agencyName = stripMerged(next.agencyName);
  next.branchName = stripMerged(next.branchName);
  delete next.password;
  delete next.stickerPhoto;
  delete next.ffcCertificateDocument;
  delete next.realEstateLicenceDocument;
  delete next.verificationLogo;
  // Demo roster fields — never persist the whole launcher list on the session user.
  delete next._options;
  delete next._label;
  delete next._propertyCount;
  delete next._pinned;
  delete next._demoToken;
  const photo = sessionMedia(next.photo);
  const logo = sessionMedia(next.logo);
  if (photo) next.photo = photo;
  else delete next.photo;
  if (logo) next.logo = logo;
  else delete next.logo;
  if (next.partnerProfile && typeof next.partnerProfile === 'object') {
    const pp = { ...next.partnerProfile };
    delete pp.pageContent;
    if (isDataUrl(pp.logoUrl)) delete pp.logoUrl;
    if (isDataUrl(pp.heroImageUrl)) delete pp.heroImageUrl;
    next.partnerProfile = pp;
  }
  if (next.agencyStats) {
    next.agencyStats = {
      totalRevenue: next.agencyStats.totalRevenue,
      propertiesSold: next.agencyStats.propertiesSold,
      activeAgents: next.agencyStats.activeAgents,
      totalListings: next.agencyStats.totalListings,
      activeLeads: next.agencyStats.activeLeads,
      crmLeadCount: next.agencyStats.crmLeadCount,
      branches: next.agencyStats.branches,
      teams: next.agencyStats.teams,
    };
  }
  if (next.agentStats) {
    next.agentStats = {
      myCommission: next.agentStats.myCommission,
      activeListings: next.agentStats.activeListings,
      pendingDeals: next.agentStats.pendingDeals,
      meetingsScheduled: next.agentStats.meetingsScheduled,
      crmLeadCount: next.agentStats.crmLeadCount,
    };
  }
  if (next.partnerStats && typeof next.partnerStats === 'object') {
    const ps = { ...next.partnerStats };
    delete ps.crmLeads;
    delete ps.pipelineDeals;
    next.partnerStats = ps;
  }
  return next;
}

function minimalSession(data) {
  return {
    _id: data._id,
    id: data.id,
    name: data.name,
    email: data.email,
    role: data.role,
    token: data.token,
    partnerType: data.partnerType,
    productLine: data.productLine,
    membershipGrade: data.membershipGrade,
    agencyName: data.agencyName,
    agencyId: data.agencyId,
    enterpriseId: data.enterpriseId,
    partnerId: data.partnerId,
    featureAccess: data.featureAccess,
    dealLimit: data.dealLimit,
    whiteLabel: data.whiteLabel,
    brandKit: data.brandKit,
    subscriptionStatus: data.subscriptionStatus,
    subscriptionPlan: data.subscriptionPlan,
    termsAcceptedAt: data.termsAcceptedAt,
    termsConsent: data.termsConsent,
    photo: sessionMedia(data.photo),
    logo: sessionMedia(data.logo),
    ...(data._operatedByAdmin ? { _operatedByAdmin: true } : {}),
    ...(data._demoImpersonation ? { _demoImpersonation: true } : {}),
  };
}

export function persistUserSession(data, { loginExperience } = {}) {
  const write = (obj) => {
    localStorage.setItem('user', JSON.stringify(obj));
  };
  try {
    write(slimSession(data));
  } catch (err) {
    if (!isQuotaError(err)) throw err;
    try {
      localStorage.removeItem('user');
      write(minimalSession(data));
    } catch (err2) {
      if (!isQuotaError(err2)) throw err2;
      localStorage.removeItem('user');
      throw err2;
    }
  }
  if (typeof window !== 'undefined') {
    rememberLoginExperience(data, loginExperience);
    rememberLoginHome(data);
    window.dispatchEvent(new Event('ipm:session-updated'));
  }
}

export function homePathForUser(u) {
  const role = String(u?.role || '').toLowerCase();
  const pt = String(u?.partnerType || '').toLowerCase();
  const membership = String(u?.productLine || '').toLowerCase() === 'membership' || !!u?.membershipGrade;
  if (role === 'admin') return '/admin';
  if (role === 'partner_enterprise') return '/partner-enterprise-dashboard';
  if (role === 'enterprise') return '/enterprise-dashboard';
  if ((role === 'partner' || role === 'partner_agent') && pt === 'bond_originator' && !membership) return '/bond-originator';
  if (role === 'partner' && pt === 'conveyancer' && !membership) return '/conveyancer';
  if (role === 'partner' || role === 'partner_agent') return '/partner-dashboard';
  if (role === 'branch_manager') return '/my-reports';
  if (usesOfficeDashboard(u)) return '/dashboard';
  if (role === 'agent' || role === 'independent_agent' || role === 'agency_agent') return '/agent-dashboard';
  if (role === 'seller') return '/dashboard';
  if (role === 'investor' || role === 'buyer') return '/investor-portfolio';
  return '/dashboard';
}

export { isQuotaError };
